import {Button} from '@material-ui/core';
import {Empty, fromResult, toEither, toPage} from 'Core/models/Async';
import 'Enrollment/components/EnrollmentFileProcessing/AWDStatusDetails.sass';
import {AWDHistoryDetail} from 'Enrollment/models/AWDStatusDetails';
import {sequenceS} from 'fp-ts/lib/Apply';
import * as E from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import * as qs from 'query-string';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useHistory} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import CardField from 'Shared/components/CardField';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {renderAsync} from 'Shared/components/RenderAsync';
import {toShortDateTimeWithSeconds} from 'Shared/utilities/DateFormatter';
import {
  changeEnrollmentFileAWDHistoryDetail,
  getEnrollmentFileAWDHistoryDetail,
  getEnrollmentFileApplicationDetailById,
  changeEnrollmentFilesApplicationDetail,
} from '../../ducks/enrollmentFileProcessing';
import './EnrollmentFileDetails.sass';
import StatusDetailsModal from 'Enrollment/components/EnrollmentFileProcessing/EnrollmentProcessingStatusDetailsModal';
import {EnrollmentFileApplicationDetail} from 'Enrollment/models/EnrollmentFileApplicationDetails';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import {FinalDispositionStatus} from 'Shared/constants/FinalDispositionStatus';
import {EnrollmentApplicationStatus} from 'Shared/constants/EnrollmentApplicationStatus';

type EnrollmentFileAWDHistoryDetailsProps = {
  applicationDetails: AWDHistoryDetail;
  enrollmentApplications: EnrollmentFileApplicationDetail;
};

const AWDStatusDetails = () => {
  const {AWDHistoryDetails, enrollmentFileApplicationDetail} = useSelector(
    (state: ReducerState) => state.enrollmentFileProcessing
  );
  const history = useHistory();
  const dispatch = useDispatch();
  const {search} = useLocation();
  const containsSpecialCharacter = /[^A-Za-z0-9]+/;
  const {awdSystemId, caseRequestId, id} = qs.parse(search) as {awdSystemId: string; caseRequestId: string; id: string};
  const finalDispositionStatus = (qs.parse(search).finalDispositionStatus as unknown) as FinalDispositionStatus;
  const statusId = (qs.parse(search).statusId as unknown) as EnrollmentApplicationStatus;
  useBreadcrumbs(
    'AWD Status Details',
    EnrollmentRouteConstants.ENROLLMENT_FILE_APPLICATION_AWD_STATUS_DETAILS(
      id!,
      containsSpecialCharacter.test(caseRequestId) ? 'invalidCaseRequestId' : caseRequestId!,
      awdSystemId!,
      finalDispositionStatus,
      statusId
    )
  );
  useEffect(() => {
    if (!caseRequestId) {
      history.push(EnrollmentRouteConstants.ENROLLMENT_FILE_PROCESSING);
    }
    if (!awdSystemId || !id) {
      history.push(
        EnrollmentRouteConstants.ENROLLMENT_FILE_APPLICATION_STATUS_DETAILS(
          containsSpecialCharacter.test(caseRequestId) ? 'invalidCaseRequestId' : caseRequestId,
          finalDispositionStatus,
          statusId
        )
      );
    }
  }, [caseRequestId, awdSystemId, id, finalDispositionStatus, history, statusId]);

  useEffect(() => {
    if (awdSystemId) {
      dispatch(getEnrollmentFileAWDHistoryDetail(awdSystemId));
    }
    if (caseRequestId && id) {
      dispatch(getEnrollmentFileApplicationDetailById(id, caseRequestId));
    }
    return () => {
      dispatch(changeEnrollmentFileAWDHistoryDetail(new Empty()));
      dispatch(changeEnrollmentFilesApplicationDetail(new Empty()));
    };
  }, [id, awdSystemId, caseRequestId]);

  const ApplicationsPage = ({applicationDetails, enrollmentApplications}: EnrollmentFileAWDHistoryDetailsProps) => {
    const [statusDetails, setStatusDetails] = useState<O.Option<string>>(O.none);

    return (
      <>
        <h2 className="enrollment-file-details-header">AWD Status Details</h2>
        <div className="enrollment-file-details__file-proccessing-tasks">
          <div className="enrollment-file-details__file-details">
            <CardField
              className="enrollment-file-details__details-field"
              name="Request Type"
              value={enrollmentApplications.requestType}
            />
            <CardField
              className="enrollment-file-details__details-field"
              name="SSN"
              value={enrollmentApplications.maskedIdentifier}
            />
            <CardField
              className="enrollment-file-details__details-field"
              name="First Name"
              value={enrollmentApplications.firstName}
            />
            <CardField
              className="enrollment-file-details__details-field"
              name="Last Name"
              value={enrollmentApplications.lastName}
            />
            <CardField
              className="enrollment-file-details__details-field"
              name="Product"
              value={enrollmentApplications.product}
            />
          </div>
          <PagedView
            pagedViewKey="awdStatusDetails"
            data={fromResult(toPage(applicationDetails!.awdDetails!))}
            loadMore={() => {}}
          >
            {columnProps => (
              <>
                <Column {...columnProps} title="Created Date" default>
                  {awdHistoryResult => toShortDateTimeWithSeconds(awdHistoryResult.createDate!)}
                </Column>
                <Column {...columnProps} title="Status" default>
                  {awdHistoryResult => awdHistoryResult.status}
                </Column>
                <Column {...columnProps} title="Actions" default>
                  {awdHistoryResult =>
                    awdHistoryResult.statusDetails && (
                      <Button
                        color="primary"
                        onClick={() => {
                          setStatusDetails(O.some(awdHistoryResult.statusDetails!));
                        }}
                      >
                        View Status Details
                      </Button>
                    )
                  }
                </Column>
              </>
            )}
          </PagedView>
          <StatusDetailsModal
            statusDetails={O.getOrElse(() => '')(statusDetails)}
            onClose={() => setStatusDetails(O.none)}
          />
        </div>
      </>
    );
  };

  return pipe(
    sequenceS(E.either)({
      AWDHistoryDetails: toEither(AWDHistoryDetails),
      enrollmentFileApplicationDetail: toEither(enrollmentFileApplicationDetail),
    }),

    E.fold(renderAsync, ({AWDHistoryDetails, enrollmentFileApplicationDetail}) => {
      return (
        <ApplicationsPage
          applicationDetails={AWDHistoryDetails}
          enrollmentApplications={enrollmentFileApplicationDetail}
        />
      );
    })
  );
};

export default AWDStatusDetails;
